import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Stack, Chip } from '@mui/material';
import { ReportProblem as IncidentIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WidgetCard from '../WidgetCard';
import { SiteIncidentService } from '../../../services/SiteIncidentService';
import {
  SiteIncident,
  SEVERITY_LABEL,
  STATUS_LABEL,
} from '../../../models/SiteIncident';

interface IncidentsWidgetProps {
  projectId: number;
  /** 장애 행 클릭 시 상세 모달을 띄우는 콜백 (개요에서 바로 보기). */
  onItemClick?: (incident: SiteIncident) => void;
}

const SEVERITY_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'success',
  high: 'warning',
  critical: 'error',
};

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  open: 'warning',
  investigating: 'info',
  resolved: 'success',
  closed: 'default',
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.substring(0, 10);
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
};

const IncidentsWidget: React.FC<IncidentsWidgetProps> = ({ projectId, onItemClick }) => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<SiteIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    SiteIncidentService.list(projectId)
      .then((data) => {
        if (!cancelled) setIncidents(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const openCount = useMemo(
    () =>
      incidents.filter(
        (i) =>
          i.status === 'open' ||
          i.status === 'investigating' ||
          i.status === 'in_progress' ||
          i.status === 'on_hold',
      ).length,
    [incidents],
  );
  const recent = incidents.slice(0, 3);

  return (
    <WidgetCard
      title={`장애 이력 ${incidents.length > 0 ? `(진행중 ${openCount}/${incidents.length})` : ''}`}
      icon={<IncidentIcon fontSize="small" />}
      actionLabel="이력 전체"
      onAction={() => navigate(`/project/${projectId}/incidents`)}
      testId="widget-incidents"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : recent.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          기록된 장애 이력이 없습니다.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {recent.map((i) => (
            <Box
              key={i.id}
              onClick={onItemClick ? () => onItemClick(i) : undefined}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                ...(onItemClick && {
                  cursor: 'pointer',
                  borderRadius: 1,
                  mx: -0.5,
                  px: 0.5,
                  '&:hover': { bgcolor: 'action.hover' },
                }),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
                {formatDate(i.occurred_at)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip
                  label={SEVERITY_LABEL[i.severity]}
                  size="small"
                  color={SEVERITY_COLOR[i.severity] ?? 'default'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Chip
                  label={STATUS_LABEL[i.status]}
                  size="small"
                  color={STATUS_COLOR[i.status] ?? 'default'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {i.title}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </WidgetCard>
  );
};

export default IncidentsWidget;
